"use client";

import {
  TrendingUp,
  FileText,
  Shield,
  Calendar,
  AlertTriangle,
  GitCompareArrows,
} from "lucide-react";

interface TreatmentUpdateProps {
  summary: string;
  changes: Array<{ drug: string; detail: string; year?: string }>;
}

interface KeyPaper {
  title: string;
  journal: string;
  year: string;
  pmid?: string;
  oneLiner: string;
  impact: string;
}

interface InsuranceProps {
  covered: string[];
  uncovered?: string[];
  notes?: string;
}

interface PracticalProtocolProps {
  schedule: string;
  followUp: string;
  switchCriteria?: string;
}

interface ComplicationItem {
  situation: string;
  action: string;
}

interface QuickDifferentialItem {
  disease: string;
  keyDifference: string;
}

export interface QuickClinicalData {
  treatmentUpdate?: TreatmentUpdateProps;
  keyPapers?: KeyPaper[];
  insurance?: InsuranceProps;
  practicalProtocol?: PracticalProtocolProps;
  complicationMgmt?: ComplicationItem[];
  quickDifferential?: QuickDifferentialItem[];
}

function SectionWrapper({
  icon: Icon,
  title,
  color,
  tocId,
  children,
}: {
  icon: React.ElementType;
  title: string;
  color: string;
  tocId: string;
  children: React.ReactNode;
}) {
  return (
    <div
      id={tocId}
      data-toc-id={tocId}
      className={`mb-5 scroll-mt-20 rounded-xl border ${color} p-4`}
    >
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 opacity-70" />
        <p className="text-xs font-bold uppercase tracking-wider">{title}</p>
      </div>
      {children}
    </div>
  );
}

function TreatmentUpdateSection({ summary, changes }: TreatmentUpdateProps) {
  return (
    <SectionWrapper
      icon={TrendingUp}
      title="최신 치료 트렌드"
      color="border-clinical-blue/30 bg-clinical-blue/5 text-clinical-blue"
      tocId="treatment-update"
    >
      <p className="mb-3 text-sm text-foreground">{summary}</p>
      <div className="space-y-2">
        {changes.map((c, i) => (
          <div
            key={i}
            className="flex items-start gap-3 rounded-lg bg-background/60 px-3 py-2"
          >
            <span className="mt-0.5 shrink-0 rounded bg-clinical-blue/15 px-1.5 py-0.5 text-[11px] font-semibold text-clinical-blue">
              {c.year || "NEW"}
            </span>
            <div>
              <span className="text-sm font-semibold text-foreground">
                {c.drug}
              </span>
              <span className="text-sm text-muted-foreground">
                {" — "}
                {c.detail}
              </span>
            </div>
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
}

function KeyPapersSection({ papers }: { papers: KeyPaper[] }) {
  return (
    <SectionWrapper
      icon={FileText}
      title="핵심 논문 하이라이트"
      color="border-clinical-purple/30 bg-clinical-purple/5 text-clinical-purple"
      tocId="key-papers"
    >
      <div className="space-y-3">
        {papers.map((p, i) => (
          <div key={i} className="rounded-lg bg-background/60 px-3 py-2.5">
            <div className="mb-1 flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-foreground leading-snug">
                {p.pmid ? (
                  <a
                    href={`https://pubmed.ncbi.nlm.nih.gov/${p.pmid}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-clinical-purple hover:underline"
                  >
                    {p.title}
                  </a>
                ) : (
                  p.title
                )}
              </p>
            </div>
            <p className="mb-1.5 text-xs text-muted-foreground">
              {p.journal}, {p.year}
            </p>
            <p className="text-sm text-foreground">{p.oneLiner}</p>
            <p className="mt-1 text-xs font-medium text-clinical-purple">
              → {p.impact}
            </p>
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
}

function InsuranceSection({ covered, uncovered, notes }: InsuranceProps) {
  return (
    <SectionWrapper
      icon={Shield}
      title="보험 기준 (건강보험 심사기준)"
      color="border-clinical-green/30 bg-clinical-green/5 text-clinical-green"
      tocId="insurance"
    >
      <div className="space-y-2">
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-clinical-green">
            급여 인정
          </p>
          <ul className="space-y-1">
            {covered.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-clinical-green" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        {uncovered && uncovered.length > 0 && (
          <div className="mt-3">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-clinical-red">
              비급여 / 제한
            </p>
            <ul className="space-y-1">
              {uncovered.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-clinical-red" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
        {notes && (
          <p className="mt-2 rounded-lg bg-background/60 px-3 py-2 text-xs text-muted-foreground">
            {notes}
          </p>
        )}
      </div>
    </SectionWrapper>
  );
}

function PracticalProtocolSection({
  schedule,
  followUp,
  switchCriteria,
}: PracticalProtocolProps) {
  return (
    <SectionWrapper
      icon={Calendar}
      title="실전 프로토콜"
      color="border-clinical-amber/30 bg-clinical-amber/5 text-clinical-amber"
      tocId="practical-protocol"
    >
      <div className="space-y-3">
        <div className="rounded-lg bg-background/60 px-3 py-2">
          <p className="mb-1 text-xs font-semibold text-clinical-amber">
            주사/치료 스케줄
          </p>
          <p className="text-sm text-foreground whitespace-pre-line">{schedule}</p>
        </div>
        <div className="rounded-lg bg-background/60 px-3 py-2">
          <p className="mb-1 text-xs font-semibold text-clinical-amber">
            추적 검사
          </p>
          <p className="text-sm text-foreground whitespace-pre-line">{followUp}</p>
        </div>
        {switchCriteria && (
          <div className="rounded-lg bg-background/60 px-3 py-2">
            <p className="mb-1 text-xs font-semibold text-clinical-amber">
              치료 전환 기준
            </p>
            <p className="text-sm text-foreground whitespace-pre-line">
              {switchCriteria}
            </p>
          </div>
        )}
      </div>
    </SectionWrapper>
  );
}

function ComplicationMgmtSection({ items }: { items: ComplicationItem[] }) {
  return (
    <SectionWrapper
      icon={AlertTriangle}
      title="합병증 대처"
      color="border-clinical-red/30 bg-clinical-red/5 text-clinical-red"
      tocId="complication-mgmt"
    >
      <div className="space-y-2">
        {items.map((item, i) => (
          <div
            key={i}
            className="rounded-lg bg-background/60 px-3 py-2"
          >
            <p className="text-sm font-semibold text-foreground">
              {item.situation}
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              → {item.action}
            </p>
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
}

function QuickDifferentialSection({
  items,
}: {
  items: QuickDifferentialItem[];
}) {
  return (
    <SectionWrapper
      icon={GitCompareArrows}
      title="감별 Quick Table"
      color="border-border bg-muted/30 text-foreground"
      tocId="quick-differential"
    >
      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted">
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                감별 질환
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                핵심 감별점
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-3 py-2 font-medium">{item.disease}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {item.keyDifference}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionWrapper>
  );
}

export function QuickClinicalView({ data }: { data: QuickClinicalData }) {
  return (
    <div className="space-y-0">
      {data.treatmentUpdate && (
        <TreatmentUpdateSection {...data.treatmentUpdate} />
      )}
      {data.keyPapers && data.keyPapers.length > 0 && (
        <KeyPapersSection papers={data.keyPapers} />
      )}
      {data.insurance && <InsuranceSection {...data.insurance} />}
      {data.practicalProtocol && (
        <PracticalProtocolSection {...data.practicalProtocol} />
      )}
      {data.complicationMgmt && data.complicationMgmt.length > 0 && (
        <ComplicationMgmtSection items={data.complicationMgmt} />
      )}
      {data.quickDifferential && data.quickDifferential.length > 0 && (
        <QuickDifferentialSection items={data.quickDifferential} />
      )}
    </div>
  );
}
